import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  FormGroup,
  Label,
  Row,
} from "reactstrap";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { FaRegEye } from "react-icons/fa";
import CompanySelector from "ATSComponents/Common/CompanySelector";
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer";
import axios from "axios";
import CandidateLogsTimelineModal from "./CandidateLogsTimelineModal";
import ViewCandidatePopupModal, { normalizeStageLogs } from "./ViewCandidatesPopupModal";

const SelectedCandidates = () => {
  document.title = "ATS - Selected Candidates";

  const [candidateListData, setCandidateListData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 0,
  });
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [viewCandidatePopupShow, setViewCandidatePopupShow] = useState(false);
  const [logsTimelineShow, setLogsTimelineShow] = useState(false);
  const [selectedLogsCandidate, setSelectedLogsCandidate] = useState(null);
  const [selectedCandidateDetails, setSelectedCandidateDetails] = useState(null);
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);

  const userInfos = JSON.parse(sessionStorage.getItem("atsuser-info"));
  const initialCompanyId =
    userInfos?.user_details?.companies_data?.[0]?.company_id;

  useEffect(() => {
    if (initialCompanyId) {
      setCompanyId(initialCompanyId);
    }
  }, [initialCompanyId]);

  useEffect(() => {
    if (!companyId) return;

    const fetchCandidateList = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          "/api/v1/candidates/candidates-by-status/",
          {
            params: {
              status: "Placed",
              company_id: companyId,
              page: lazyParams.page + 1,
              size: lazyParams.rows,
            },
          }
        );

        setCandidateListData(response.data.results || []);
        setTotalRecords(response.data.count || 0);
      } catch (error) {
        console.error("Error fetching candidate list:", error);
        setCandidateListData([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateList();
  }, [companyId, lazyParams]);

  const handleCompanyChange = company => {
    if (company?.company_id) {
      setCompanyId(company.company_id);
    }
  };

  const handleViewCandidate = (candidate = null) => {
    if (candidate) {
      const rowIndex = candidateListData.findIndex(
        item => (item.candidate_id || item.id) === (candidate.candidate_id || candidate.id)
      );
      setSelectedCandidateDetails(candidate);
      setSelectedCandidateIndex(rowIndex >= 0 ? rowIndex : null);
      setViewCandidatePopupShow(true);
      return;
    }

    setViewCandidatePopupShow(false);
    setSelectedCandidateDetails(null);
    setSelectedCandidateIndex(null);
  };

  const handlePreviousCandidate = () => {
    if (selectedCandidateIndex === null || selectedCandidateIndex <= 0) return;
    const nextIndex = selectedCandidateIndex - 1;
    setSelectedCandidateDetails(candidateListData[nextIndex]);
    setSelectedCandidateIndex(nextIndex);
  };

  const handleNextCandidate = () => {
    if (selectedCandidateIndex === null || selectedCandidateIndex >= candidateListData.length - 1) return;
    const nextIndex = selectedCandidateIndex + 1;
    setSelectedCandidateDetails(candidateListData[nextIndex]);
    setSelectedCandidateIndex(nextIndex);
  };

  const handleViewLogs = rowData => {
    setSelectedLogsCandidate(rowData);
    setLogsTimelineShow(true);
  };

  const viewCandidateExperience = rowData => {
    const [yearPart, monthPart = "0"] = String(rowData?.total_experience || "0.0").split(".");
    const years = Number(yearPart) || 0;
    const months = Number(monthPart[0]) || 0;

    return (
      <span className="bg-light px-2 py-1 rounded d-inline-block">
        {years} {years === 1 ? "Year" : "Years"}
        {months > 0 && ` ${months} ${months === 1 ? "Month" : "Months"}`}
      </span>
    );
  };

  const viewCandidateDetails = rowData => (
    <Button
      className="btn btn-light"
      onClick={() => handleViewCandidate(rowData)}
      title="View Candidate Details"
    >
      <FaRegEye />
    </Button>
  );

  const stageLogsTemplate = rowData => {
    const logs = normalizeStageLogs(rowData);
    const latestLog = logs[logs.length - 1];

    if (!latestLog) {
      return <span className="text-muted small">No logs</span>;
    }

    return (
      <button
        type="button"
        className="text-start"
        onClick={() => handleViewLogs(rowData)}
        title="View logs timeline"
        style={{
          minWidth: 220,
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          background: "#f8fafc",
          padding: "8px 10px",
          cursor: "pointer",
          width: "100%",
        }}
      >
        <div className="fw-semibold" style={{ color: "#1e293b", fontSize: 13 }}>
          {latestLog.stage}
        </div>
        <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: 12 }}>
          {latestLog.jobTitle}
        </div>
        <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: 12 }}>
          Reason : {latestLog.reason || "-"}
        </div>
         <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: 12 }}>
          Updated By : {latestLog.createdBy || "-"}
        </div>
        <div className="text-muted text-truncate" style={{ maxWidth: 260, fontSize: 12 }}>
          Updated At : {latestLog.timestamp || "-"}
        </div>
      </button>
    );
  };

  const viewIsSelectedStatus = () => (
    <span className="fw-semibold text-success">Yes</span>
  );

  const getNameInitials = name =>
    name
      ? name
          .trim()
          .split(/\s+/)
          .map(part => part[0])
          .slice(0, 2)
          .join("")
          .toUpperCase()
      : "?";
  const getInitialsStyle = name => {
    const palette = [
      { background: "#eef2ff", color: "#4f46e5", borderColor: "#c7d2fe" },
      { background: "#fce7f3", color: "#be185d", borderColor: "#f9a8d4" },
      { background: "#dcfce7", color: "#15803d", borderColor: "#86efac" },
      { background: "#ffedd5", color: "#c2410c", borderColor: "#fdba74" },
      { background: "#cffafe", color: "#0e7490", borderColor: "#67e8f9" },
    ];
    const index = String(name || "")
      .split("")
      .reduce((sum, char) => sum + char.charCodeAt(0), 0) % palette.length;
    return palette[index];
  };

  const candidateNameTemplate = rowData => {
  const candidateName = [
    rowData.first_name,
    rowData.middle_name,
    rowData.last_name,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="candidate-name-with-initials">
      <span
        className="candidate-name-initials"
        style={getInitialsStyle(candidateName)}
      >
        {getNameInitials(candidateName)}
      </span>
      <span
        onClick={() => handleViewCandidate(rowData)}
        style={{
          color: "#0d6efd",
          cursor: "pointer",
          fontWeight: 600,
          textDecoration: "underline",
        }}
        title="View Resume"
      >
        {candidateName}
      </span>
    </div>
  );
};

  const tableHeader = (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
      <div>
        <h5 className="mb-1 fw-semibold">Placed candidate list</h5>
        <span className="text-muted small">
          Review selected candidates, latest stage logs, and resume previews.
        </span>
      </div>
      <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-2">
        Total: {totalRecords}
      </span>
    </div>
  );

  return (
    <Container fluid className="page-content">
      <div className="page-title-box mb-0 recruiter-dashboard">
        <Row className="align-items-center justify-content-between mb-3">
          <Col>
            <h1 className="page-title mb-1">Selected Candidates</h1>
            <p className="text-muted mb-0">
              Clean overview of placed candidates with logs and resume preview.
            </p>
          </Col>
          <Col xxl={3} xl={3} lg={3} md={4} sm={12} className="text-end">
            <FormGroup className="mb-0 text-start">
              <Label for="CompanySelect" className="fw-semibold d-block">
                Select Company <span className="text-danger">*</span>
              </Label>
              <CompanySelector onCompanyChange={handleCompanyChange} />
            </FormGroup>
          </Col>
        </Row>

        {loading ? (
          <OrgTableShimmer columns={5} rows={7} titleWidth="210px" />
        ) : (
          <Card className="mb-3 shadow-md border-0 p-2">
            <DataTable
              value={candidateListData}
              header={tableHeader}
              dataKey="candidate_id"
              lazy
              paginator
              rows={lazyParams.rows}
              first={lazyParams.first}
              totalRecords={totalRecords}
              onPage={setLazyParams}
              responsiveLayout="scroll"
              rowsPerPageOptions={[5, 10, 20, totalRecords]}
              selection={selectedCandidates}
              onSelectionChange={e => setSelectedCandidates(e.value)}
              id="AllEmployees_TableView"
            >
              <Column header="Name" body={candidateNameTemplate} />
              <Column field="email" header="Email" />
              <Column field="mobile_phone" header="Phone" />
              <Column header="Logs" body={stageLogsTemplate} />
              <Column
                field="total_experience"
                header="Experience"
                body={viewCandidateExperience}
              />
              <Column header="Candidate Details" body={viewCandidateDetails} />
              <Column header="Is Selected" body={viewIsSelectedStatus} />
            </DataTable>
          </Card>
        )}
      </div>

      <ViewCandidatePopupModal
        isOpen={viewCandidatePopupShow}
        toggle={() => handleViewCandidate()}
        candidate={selectedCandidateDetails}
        onPrevious={handlePreviousCandidate}
        onNext={handleNextCandidate}
        previousDisabled={selectedCandidateIndex === null || selectedCandidateIndex <= 0}
        nextDisabled={selectedCandidateIndex === null || selectedCandidateIndex >= candidateListData.length - 1}
      />
      <CandidateLogsTimelineModal
        isOpen={logsTimelineShow}
        toggle={() => setLogsTimelineShow(false)}
        candidate={selectedLogsCandidate}
      />
    </Container>
  );
};

export default SelectedCandidates;
